/**
 * WHAT: Reusable wide modal component rendered via React portal.
 * WHY: Many DAMA views (attestation tables, compliance reports, data grids) need
 *      wide modals to display tabular data comfortably. This component standardizes
 *      the modal pattern across the application with consistent styling.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import './WideModal.css';

/**
 * WHAT: Portal-based modal with configurable header, body, footer, and max width.
 * WHY: Centralizes the wide-modal pattern so every feature (attestation, compliance,
 *      SQL executor) uses consistent overlay styling, close behavior, and responsive layout.
 */
const WideModal = ({
  show,
  onClose,
  title,
  children,
  footer,
  headerIcon,
  headerClass = 'bg-primary text-white',
  maxWidth = '1400px',
  bodyStyle
}) => {
  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      className="wide-modal-overlay"
      onClick={onClose}
    >
      <div
        className="wide-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content wide-modal-content" style={{ maxWidth }}>
          <div className={`modal-header ${headerClass}`}>
            <h5 className="modal-title">
              {headerIcon && <i className={`fas ${headerIcon} me-2`}></i>}
              {title}
            </h5>
            {/* Explicit "×" glyph instead of Bootstrap's .btn-close — the
                latter draws the X via a background-image SVG that renders
                invisible on dark headers (the white filter doesn't always
                apply). Using a character that inherits the header text color
                guarantees visibility on both light and dark headers. */}
            <button
              type="button"
              className="btn-close-glyph"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.6rem',
                lineHeight: 1,
                cursor: 'pointer',
                color: 'inherit',
                opacity: 0.85,
                padding: '0 0.25rem',
              }}
            >&times;</button>
          </div>
          <div className="modal-body" style={bodyStyle}>
            {children}
          </div>
          {footer && (
            <div className="modal-footer">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WideModal;
